import React, { useState, useEffect, useRef, useCallback } from 'react';
import StorageHelper from '../../../utils/storage-helper';

const AUTOPLAY_MS = 5000;

// Ad records sometimes carry placeholder text such as "." — only show text that has real content.
const hasText = (value) => typeof value === 'string' && /[A-Za-z0-9]/.test(value);

export default function AdvertisementGallery() {
    const [ads, setAds] = useState(null);
    const [active, setActive] = useState(0);
    const [paused, setPaused] = useState(false);
    const trackRef = useRef(null);

    useEffect(() => {
        fetch(process.env.REACT_APP_DOVESAPI + 'advertisement/all', {
            headers: { Authorization: 'Bearer ' + StorageHelper.get('token') },
        })
            .then((response) => response.json())
            .then((data) => setAds(Array.isArray(data.responseBody) ? data.responseBody : []))
            .catch((error) => {
                console.error('Error fetching advertisements:', error);
                setAds([]);
            });
    }, []);

    const count = ads ? ads.length : 0;

    const goTo = useCallback((index) => {
        const track = trackRef.current;
        if (!track || count === 0) return;
        const next = (index + count) % count;
        track.scrollTo({ left: next * track.clientWidth, behavior: 'smooth' });
        setActive(next);
    }, [count]);

    const handleScroll = (event) => {
        const track = event.currentTarget;
        if (track.clientWidth) setActive(Math.round(track.scrollLeft / track.clientWidth));
    };

    // Autoplay only starts once real slides exist, and pauses while the user is interacting.
    useEffect(() => {
        if (count < 2 || paused) return undefined;
        const timer = setInterval(() => goTo(active + 1), AUTOPLAY_MS);
        return () => clearInterval(timer);
    }, [count, paused, active, goTo]);

    if (ads === null) {
        return <div className="skeleton absolute inset-0 rounded-2xl" aria-busy="true" />;
    }

    if (count === 0) {
        return (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 rounded-2xl bg-gradient-to-br from-primary/10 to-base-200 text-center p-6">
                <span className="badge badge-primary badge-outline badge-sm uppercase font-bold tracking-wider">Doves</span>
                <p className="text-lg font-bold text-base-content">No announcements right now</p>
                <p className="text-sm text-base-content/70">Check back soon for news and offers.</p>
            </div>
        );
    }

    return (
        <div
            className="absolute inset-0 rounded-2xl overflow-hidden group"
            onMouseEnter={() => setPaused(true)}
            onMouseLeave={() => setPaused(false)}
            onFocus={() => setPaused(true)}
            onBlur={() => setPaused(false)}
        >
            <div ref={trackRef} onScroll={handleScroll} className="carousel w-full h-full" aria-roledescription="carousel">
                {ads.map((ad, index) => {
                    const showTitle = hasText(ad.title);
                    const showDesc = hasText(ad.description);
                    const hasCta = hasText(ad.callToActionURL);
                    const overlay = showTitle || showDesc || hasCta;
                    return (
                        <div
                            key={ad.id ?? index}
                            className="carousel-item relative w-full h-full"
                            role="group"
                            aria-roledescription="slide"
                            aria-label={`${index + 1} of ${count}`}
                        >
                            <img
                                src={ad.image}
                                alt={showTitle ? ad.title : 'Advertisement'}
                                className="absolute inset-0 w-full h-full object-cover"
                                draggable={false}
                            />
                            {overlay && (
                                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-neutral/90 via-neutral/60 to-transparent px-6 pt-16 pb-14 text-neutral-content">
                                    <span className="badge badge-primary badge-sm uppercase font-bold tracking-wider mb-2">Ad</span>
                                    {showTitle && <h3 className="text-xl font-bold leading-tight">{ad.title}</h3>}
                                    {showDesc && <p className="mt-1 text-sm text-neutral-content/80 line-clamp-2">{ad.description}</p>}
                                    {hasCta && (
                                        <a
                                            href={ad.callToActionURL}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="btn btn-primary btn-sm mt-3"
                                        >
                                            Learn more
                                        </a>
                                    )}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            {count > 1 && (
                <>
                    <button
                        type="button"
                        aria-label="Previous advertisement"
                        onClick={() => goTo(active - 1)}
                        className="btn btn-circle btn-sm absolute left-3 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity"
                    >
                        ❮
                    </button>
                    <button
                        type="button"
                        aria-label="Next advertisement"
                        onClick={() => goTo(active + 1)}
                        className="btn btn-circle btn-sm absolute right-3 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity"
                    >
                        ❯
                    </button>
                    <div className="absolute bottom-4 inset-x-0 flex justify-center gap-2">
                        {ads.map((ad, index) => (
                            <button
                                key={ad.id ?? index}
                                type="button"
                                aria-label={`Go to advertisement ${index + 1}`}
                                aria-current={index === active}
                                onClick={() => goTo(index)}
                                className={`h-2 rounded-full transition-all ${index === active ? 'w-6 bg-primary' : 'w-2 bg-base-100/70 hover:bg-base-100'}`}
                            />
                        ))}
                    </div>
                </>
            )}
        </div>
    );
}
